import { createActions } from 'redux-actions';
import { put, select, call, race, take } from 'redux-saga/effects';
import { LOCATION_CHANGE } from 'react-router-redux';
import {
  apiActionResult,
  apiActions as commonApiActions,
  apiResourceActionCreator
} from '../../common';
import { authTokenSelector } from '../../session';
import { API_ENDPOINT } from './endpoints';
import getTeamTasksConfig from './getTeamTasks';
import { createPollingWorker } from './pollingUtils';

const actions = createActions({
  TASK: {
    GET_TEAM_SYNC_STATUS: (
      businessId,
      businessTeamId,
      startPolling = false
    ) => ({ businessId, businessTeamId, startPolling }),
    API: {
      GET_TEAM_SYNC_STATUS: apiResourceActionCreator('teamSyncStatus')
    }
  }
});

const apiAction = (authToken, businessId, businessTeamId) =>
  commonApiActions.authenticated.get(
    `${API_ENDPOINT}/business/${businessId}/team/${businessTeamId}/sync-status`,
    authToken,
    actions.task.api.getTeamSyncStatus
  );

/**
 * Periodically polls the team synchronization status every 5 seconds
 * and refreshes team tasks once a terminal state (COMPLETED, IDLE, FAILED) is reached.
 */
const pollTeamSyncWorker = createPollingWorker({
  apiAction,
  apiTarget: actions.task.api.getTeamSyncStatus,
  loggerName: 'Team Sync Poller',
  shouldStopPolling: (payload) => {
    const status = payload.teamSyncStatus
      ? payload.teamSyncStatus.status
      : null;
    return status === 'COMPLETED' || status === 'IDLE' || status === 'FAILED';
  },
  onComplete: function* (businessId, businessTeamId) {
    yield put(
      getTeamTasksConfig.action.task.getTeamTasks(
        businessId,
        businessTeamId,
        {}
      )
    );
  }
});

function* getTeamSyncStatusSaga(action) {
  const { businessId, businessTeamId, startPolling } = action.payload;

  try {
    const authToken = yield select(authTokenSelector);
    yield put(apiAction(authToken, businessId, businessTeamId));
    const result = yield apiActionResult(actions.task.api.getTeamSyncStatus);

    if (result.success && startPolling) {
      const currentStatus =
        result.success.payload && result.success.payload.teamSyncStatus
          ? result.success.payload.teamSyncStatus.status
          : null;

      if (currentStatus === 'PENDING' || currentStatus === 'PROCESSING') {
        yield race({
          poll: call(pollTeamSyncWorker, businessId, businessTeamId),
          cancel: take(LOCATION_CHANGE)
        });
      }
    }
  } catch (e) {
    console.error('[Team Sync Saga] Context initialization failed.', e);
  }
}

/**
 * Redux configuration mapping action creators, polling sagas, and slice
 * reducers for fetching and tracking team synchronization status.
 */
const configuration = {
  name: 'getTeamSyncStatus',
  action: actions,
  sagas: getTeamSyncStatusSaga,
  reducers: {
    request: (state) => ({ ...state }),
    success: (state, action) => ({
      ...state,
      teamSyncStatus: action.payload.teamSyncStatus
    }),
    failed: (state) => ({
      ...state,
      teamSyncStatus: 'FAILED'
    })
  }
};

export default configuration;



// WEBPACK FOOTER //
// ./src/state/modules/task/configurations/getTeamSyncStatus.js